import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createQColumnHelper,
  DataProvider,
  QBox,
  QCard,
  QCenter,
  QCollapse,
  QDataTable,
  QHeading,
  QIcon,
  QIconButton,
  QStack,
  QTag,
  QTooltip,
} from "@qualio/ui-components";
import { useCurrentConfigs } from "../../hooks/useCurrentConfigs";
import { useCurrentProduct } from "../../hooks/useCurrentProduct";
import {
  FeatureFlags,
  PreviousApprovedVersion,
  RequirementDesignElement,
} from "@design-controls/types";
import { Link, useNavigate } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import { designElementStatusColumn } from "../DesignElementStatusColumn/DesignElementStatusColumn";
import { designElementVersionColumn } from "../DesignElementVersionColumn/DesignElementVersionColumn";
import { linkedElementVersionPath } from "./linkedElementVersionPath";
import { reviewItemComparePath } from "./reviewItemComparePath";
import { ReviewItemCompareButton } from "./ReviewItemCompareButton";
import { sortByCode } from "../../sortByCode";

const columnHelper = createQColumnHelper<RequirementDesignElement>();

type DesignElement = {
  latest?: string;
  previousApprovedVersion?: PreviousApprovedVersion | null;
} & RequirementDesignElement;

type Props = {
  requirements: Array<DesignElement>;
  underReview?: boolean;
  versioned?: boolean;
  showLinkedVersions?: boolean;
  showVersionCompare?: boolean;
};

export const RequirementTable: React.FC<Props> = ({
  requirements,
  underReview,
  versioned = false,
  showLinkedVersions = false,
  showVersionCompare = false,
}) => {
  const { t } = useTranslation();
  const configs = useCurrentConfigs();
  const [expanded, setExpanded] = useState(true);
  const navigate = useNavigate();
  const features = useFlags();
  const { id: productId } = useCurrentProduct();

  const onClickToDiff = useCallback(
    (item: DesignElement) => {
      return () => navigate(`${item.id}`);
    },
    [navigate],
  );

  const columns = useMemo(() => {
    const columns = [
      columnHelper.code("code", {
        header: t("requirements.list.table.header.id"),
        minWidth: "100px",
        width: "10%",
      }),
      {
        accessorKey: "title",
        header: t("requirements.list.table.header.title"),
        meta: {
          minWidth: "300px",
          width: "30%",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: RequirementDesignElement };
        }) => {
          return (
            <Link
              to={`/product/${productId}/requirement/${
                row.id
              }${linkedElementVersionPath(row, versioned)}`}
              target="_blank"
            >
              {row.title}
            </Link>
          );
        },
      },
      columnHelper.text((record) => record.component?.label, {
        header: t("requirements.list.table.header.component"),
        minWidth: "150px",
        width: "10%",
      }),
      columnHelper.text((record) => record.category?.label, {
        header: t("requirements.list.table.header.category"),
        minWidth: "150px",
        width: "10%",
      }),
      {
        header: t("requirements.list.table.header.type"),
        meta: {
          minWidth: "150px",
          width: "10%",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: RequirementDesignElement };
        }) => {
          return (
            <QTag variantColor="gray">
              {
                (
                  configs.find((config) => config.type === row.type) as
                    | { label: string }
                    | undefined
                )?.label
              }
            </QTag>
          );
        },
      },
      ...(showLinkedVersions
        ? [designElementVersionColumn(t("common.metadata.version"), "--")]
        : []),
      {
        ...designElementStatusColumn(),
        meta: {
          minWidth: "150px",
          width: "10%",
        },
      },
    ];

    if (versioned && showVersionCompare) {
      columns.push({
        header: " ",
        id: "diff",
        meta: {
          minWidth: "50px",
          width: "10%",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: DesignElement };
        }) => {
          const comparePath = reviewItemComparePath(row);
          return comparePath ? (
            <ReviewItemCompareButton
              to={`/product/${productId}/requirement/${row.id}${comparePath}`}
            />
          ) : null;
        },
      });
    } else if (versioned && features[FeatureFlags.COMPARE_VIEW]) {
      columns.push({
        header: " ",
        id: "diff",
        meta: {
          minWidth: "50px",
          width: "10%",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: DesignElement };
        }) => {
          return (
            row.itemStatus !== "NEW" && (
              <QCenter>
                <QTooltip label={t("common.ariaLabels.seeDifference")}>
                  <QBox>
                    <QIconButton
                      iconName="GitPullRequest"
                      aria-label={t(
                        "common.ariaLabels.seeDifferenceBetweenVersion",
                      )}
                      onClick={onClickToDiff(row)}
                    />
                  </QBox>
                </QTooltip>
              </QCenter>
            )
          );
        },
      });
    }

    if (underReview) {
      columns.push({
        header: " ",
        meta: {
          minWidth: "90px",
          width: "10%",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: DesignElement };
        }) => {
          return row.latest && row.version !== row.latest ? (
            <QCenter>
              <QIcon
                iconName="FillExclamationTriangleFill"
                color="orange.500"
              />
            </QCenter>
          ) : null;
        },
      });
    }

    return columns;
  }, [
    underReview,
    versioned,
    showLinkedVersions,
    showVersionCompare,
    productId,
    onClickToDiff,
    features,
    t,
  ]);

  if (!requirements.length) {
    return null;
  }

  return (
    <QCard>
      <QStack
        direction="row"
        justifyContent="space-between"
        padding="12px 16px"
      >
        <QHeading fontSize="md" fontWeight={600}>
          {t("requirements.policy.heading")}
        </QHeading>
        <QIconButton
          onClick={() => setExpanded((s) => !s)}
          iconName={expanded ? "ChevronUp" : "ChevronDown"}
          aria-label={
            expanded
              ? t("requirements.list.collapseExpand.collapse")
              : t("requirements.list.collapseExpand.expand")
          }
        />
      </QStack>
      <QCollapse isOpen={expanded}>
        <DataProvider.Fixed data={requirements.sort(sortByCode)}>
          <QDataTable
            columns={columns}
            hideItemCount
            data-cy="requirement-list"
          />
        </DataProvider.Fixed>
      </QCollapse>
    </QCard>
  );
};
